import { ChangeEvent, useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { calculateProjectTotal, taskStatusesConfig } from 'entities';

import {
  Column,
  Row,
  Alert,
  Checkbox,
  LabeledTextarea,
  Label,
  LabeledInput,
} from 'components/Blocks';
import { SingleFileUploadButton } from 'components/Uploads';

import { TotalRow } from './TotalRow';

import { useProjectProfile } from '../context';

import { omitUndefined } from 'utils/object';

import { PhaseProps } from './types';
import { UploadedFile } from 'types';
import { setVariablesToInitialValues } from './utils';

export function Complementary({ dispatch, state, disabled }: PhaseProps) {
  const { t } = useLang();

  const { project } = useProjectProfile();

  const handlePoToggle = useCallback(() => {
    dispatch({ poNumber: ({ poNumber }) => ({ value: poNumber.value === 'N/A' ? '' : 'N/A' }) });
  }, [dispatch]);

  const handleEmailAdditionToggle = useCallback(() => {
    dispatch({ emailChainAddition: ({ emailChainAddition }) => !emailChainAddition });
  }, [dispatch]);

  const handleCanceledTasksToggle = useCallback(
    (id: string) => {
      dispatch({
        canceledTasksPriceCheckControl: ({ canceledTasksPriceCheckControl }) => ({
          ...canceledTasksPriceCheckControl,

          [id]: !canceledTasksPriceCheckControl[id],
        }),
      });
    },
    [dispatch],
  );

  const handlePoText = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      dispatch({ poNumber: ({ poNumber }) => ({ ...poNumber, value: target.value }) });
    },
    [dispatch],
  );

  const handleComments = useCallback(
    ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
      dispatch({ comment: target.value });
    },
    [dispatch],
  );

  const handleFileUpload = useCallback(
    (file: UploadedFile | null) => {
      dispatch({
        poNumber: ({ poNumber }) => omitUndefined({ ...poNumber, file: file || undefined }),
      });
    },
    [dispatch],
  );

  const handleOutsourceToggle = useCallback(
    (id: string) => {
      dispatch({
        outsourceEmailControl: ({ outsourceEmailControl }) =>
          outsourceEmailControl.map((control) =>
            control.id === id ? { ...control, added: !control.added } : control,
          ),
      });
    },
    [dispatch],
  );

  const canceledTasks = project.tasks.filter(
    ({ status }) => status === taskStatusesConfig.canceled.code,
  );

  const servicesWithInitialValues = project.services.map(({ costs, ...rest }) => ({
    ...rest,
    costs: Object.fromEntries(
      Object.entries(costs).map(([country, variables]) => [
        country,
        setVariablesToInitialValues(variables),
      ]),
    ),
  }));

  return (
    <Column gap="9">
      <Row className="phone:flex-col phone:items-stretch">
        <Row relative>
          <LabeledInput
            label={t('po-number')}
            name="poNumber"
            placeholder={t('type-here')!}
            value={state.poNumber.value}
            onChange={handlePoText}
            disabled={disabled}
            fullWidth
            className="pr-20"
          />

          <Row className="absolute bottom-2 right-2" gap="2">
            <Checkbox
              checked={state.poNumber.value === 'N/A'}
              disabled={disabled}
              size="small"
              onChange={handlePoToggle}
            />
            <Label className="text-sm">N/A</Label>
          </Row>
        </Row>

        <SingleFileUploadButton
          file={state.poNumber.file || null}
          setFile={handleFileUpload}
          uploadPrefix={`projects/${project.id}/po` as 'invoiceId'}
          disabled={state.poNumber.value === 'N/A' || disabled}
          className="self-end phone:self-stretch"
        />
      </Row>

      {!!state.outsourceEmailControl.length && (
        <Column>
          <Label>{t('outsourced-email')}</Label>

          {state.outsourceEmailControl.map(({ added, id, name }) => (
            <Column key={id} gap="2">
              <Label>{name}</Label>

              <Row gap="2">
                <Checkbox
                  size="small"
                  checked={added}
                  id={id}
                  onChange={() => handleOutsourceToggle(id)}
                  disabled={disabled}
                />

                <Label htmlFor={id}>
                  {t('have-you-added-invoice-owner-email-in-copy-with-the-client', {
                    copyWithClient: project.invoice.owner.email || 'Invoice Team',
                  })}
                </Label>
              </Row>
            </Column>
          ))}
        </Column>
      )}

      <Column gap="2">
        <Label>{t('email-chain')}</Label>

        <Row gap="2">
          <Checkbox
            id="email-chain"
            size="small"
            checked={state.emailChainAddition}
            onChange={handleEmailAdditionToggle}
            disabled={disabled}
          />

          <Label htmlFor="email-chain">
            {t('have-you-added-invoice-owner-email-in-copy-with-the-client', {
              copyWithClient: project.invoice.owner.email || 'Invoice Team',
            })}
          </Label>
        </Row>
      </Column>

      <LabeledTextarea
        label={t('comments')!}
        placeholder={t('type-here-a-comment')!}
        value={state.comment}
        onChange={handleComments}
        disabled={disabled}
      />

      <TotalRow
        title={t('project-total')}
        initialValue={calculateProjectTotal(servicesWithInitialValues)}
        currentValue={calculateProjectTotal(state.servicesActualMetrics)}
        invoiceValue={calculateProjectTotal(project.services)}
      />

      {!!canceledTasks.length && (
        <Alert level="warning" title={t('attention')}>
          {t('finish-project-with-canceled-tasks')}

          <Column gap="1" className="mt-2">
            {canceledTasks.map(({ id, name }) => (
              <Row gap="2" key={id}>
                <Checkbox
                  id={id}
                  size="small"
                  checked={state.canceledTasksPriceCheckControl[id]}
                  onChange={() => handleCanceledTasksToggle(id)}
                  color="warning"
                />

                <Label htmlFor={id} className="text-inherit">
                  {t('ive-ensured-task-current-price-definition-is-correct-to-invoice', {
                    name,
                  })}
                </Label>
              </Row>
            ))}
          </Column>
        </Alert>
      )}
    </Column>
  );
}
